import { z } from 'zod';

export const leaseSchema = z.object({
  msrp: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), {
    message: "MSRP must be a positive number"
  }),
  sellingPrice: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), {
    message: "Selling price must be a positive number"
  }),
  residualValue: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), {
    message: "Residual value must be a positive number"
  }),
  moneyFactor: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0 && Number(val) < 1), {
    message: "Money factor must be between 0 and 1"
  }),
  term: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 12 && Number(val) <= 84), {
    message: "Term must be between 12 and 84 months"
  }),
  downPayment: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
    message: "Down payment must be a non-negative number"
  }),
  taxes: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
    message: "Taxes must be a non-negative number"
  }),
  fees: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
    message: "Fees must be a non-negative number"
  }),
  dealerAddOns: z.object({
    protectionPackage: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
      message: "Protection package cost must be a non-negative number"
    }),
    serviceContract: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
      message: "Service contract cost must be a non-negative number"
    }),
    dealerInstalledEquipment: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
      message: "Dealer installed equipment cost must be a non-negative number"
    }),
    predeliveryCharge: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
      message: "Predelivery charge must be a non-negative number"
    })
  }),
  acquisitionFee: z.string().refine(val => val === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
    message: "Acquisition fee must be a non-negative number"
  })
});

export type ValidationErrors = {
  [K in keyof z.infer<typeof leaseSchema>]?: string;
} & {
  dealerAddOns?: {
    [K in keyof z.infer<typeof leaseSchema>['dealerAddOns']]?: string;
  };
};

export function validateLeaseData(data: z.infer<typeof leaseSchema>): ValidationErrors {
  try {
    leaseSchema.parse(data);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.reduce((acc, curr) => {
        const path = curr.path.join('.');
        if (path.startsWith('dealerAddOns.')) {
          const [_, field] = path.split('.');
          acc.dealerAddOns = acc.dealerAddOns || {};
          acc.dealerAddOns[field as keyof typeof acc.dealerAddOns] = curr.message;
        } else {
          acc[path as keyof ValidationErrors] = curr.message;
        }
        return acc;
      }, {} as ValidationErrors);
    }
    return {};
  }
}